import { toNumber, get, isNull, find, includes, size, merge } from "lodash"
import dayjs from "dayjs"
import React from "react"
import BigNumber from "bignumber.js"

import { APIResponse } from "redux/apis/types"
import { EQUITY_SECURITY_LIST, INT_MINI, FOREIGN_STOCK } from "config/const"

export const number = (
  num: string | number,
  decimal: number = 0,
  type: string = "floor",
  sign: boolean = false,
  def: string = "-",
  ifZeroReturnDef: boolean = false
) => {
  if (isNull(decimal)) {
    decimal = 0
  }
  if (
    //numがfalseであればdefで指定した任意の値に（0は表示させたい）
    (!num && num !== 0) ||
    //ifZeroReturnDefがtrueであれば、0か'0'はdefで指定した任意の値に
    (ifZeroReturnDef && (num === 0 || num === "0")) ||
    //INTの最小値はdefで指定した任意の値に
    num === INT_MINI
  ) {
    return def
  } else {
    let converted = decimalConverter(num, type, decimal)
    if (converted === -0) {
      converted = 0
    }
    const formatted = new Intl.NumberFormat("ja", {
      minimumFractionDigits: decimal,
    }).format(converted)
    if (sign) {
      return converted > 0 ? "+" + formatted : formatted
    } else {
      return formatted
    }
  }
}

const setNumberRules = [
  { name: "BaibaiDaka", decimals: 0, type: "floor", sign: false, digit: 10000 },
  {
    name: "BaibaiDaikin",
    decimals: 3,
    type: "floor",
    sign: false,
    digit: 1000000,
  },
  {
    name: "TradValue",
    decimals: 3,
    type: "floor",
    sign: false,
    digit: 1000000,
  },
  {
    name: "TradVolume",
    decimals: 0,
    type: "floor",
    sign: false,
    digit: 10000,
  },
  {
    name: "TotalMarketValue",
    decimals: 3,
    type: "floor",
    sign: false,
    digit: 1000000,
  },
]
export const setNumber = (data: APIResponse.Res, name: string, option = {}) => {
  const rule = find(setNumberRules, { name: name })
  if (!rule) {
    return number(get(data, name))
  } else {
    if (size(option)) merge(rule, option)
    return number(
      get(data, name) / rule.digit,
      rule.decimals,
      rule.type,
      rule.sign
    )
  }
}

export const decimalConverter = (
  num: string | number,
  type: string = "around",
  decimal: number = 0
): number => {
  const def = 0
  switch (type) {
    case "round":
      return Number(
        new BigNumber(num).decimalPlaces(decimal, BigNumber.ROUND_HALF_UP)
      )
    case "floor":
      return Number(
        new BigNumber(num).decimalPlaces(decimal, BigNumber.ROUND_DOWN)
      )
    case "ceil":
      return Number(
        new BigNumber(num).decimalPlaces(decimal, BigNumber.ROUND_UP)
      )
    default:
      return def
  }
}

export const date = (data: string | number, format: string): string => {
  return !!data ? dayjs(String(data)).format(format) : ""
}

export const upDown = (num: number | string): string => {
  const number: number = toNumber(num)
  if (number === 0 || isNaN(number) || number === INT_MINI) {
    return "equal"
  } else if (number >= 0) {
    return "up"
  } else {
    return "down"
  }
}

export const ratio = (data: object, precision: number = 2) => (
  <>
    ({number(get(data, "ChangedRatio"), precision, "floor", true)}
    <span className="unit">%</span>)
  </>
)

export const format = (fmt: string, ...args: any) => {
  var replaced = fmt
  for (var i = 0; i < args.length; i++) {
    var reg = new RegExp("\\{" + i + "\\}", "g")
    replaced = replaced.replace(reg, args[i])
  }
  return replaced
}

export const icon = (data: APIResponse.Res) => {
  const securityType = get(data, "SecurityType", false)
  if (!securityType) return ""
  const list = find(EQUITY_SECURITY_LIST, { SecurityType: securityType })
  return `icon-${list.icon}`
}

type symbolType = (data: APIResponse.Res, def?: string) => string
export const symbol: symbolType = (data, def = "") => {
  const securityType = get(data, "SecurityType")
  if (includes(FOREIGN_STOCK, securityType)) {
    return get(data, "Ticker", def)
  } else {
    return get(data, "Symbol", def)
  }
}

type GetDivisionName = (data: APIResponse.Res, isSearch?: boolean) => string
export const divisionName: GetDivisionName = (data, isSearch) => {
  const exchangeName = get(data, "ExchangeName")
  const divisionName = get(data, "DivisionName")
  const isHKGem = get(data, "Division") === "HK02"
  const isChina =
    get(data, "Exchange") === 2021 || get(data, "Exchange") === 2022
  if (isSearch) {
    return isHKGem ? `${exchangeName}${divisionName}` : exchangeName
  } else {
    return isHKGem || isChina ? `${exchangeName}${divisionName}` : exchangeName
  }
}

export function fullPrecision(value: string | number) {
  const decimalNumber = ("" + value).split(".")[1]
  if (!decimalNumber) {
    return number(value)
  }
  return number(value, decimalNumber.length)
}
